import {useCallback, useEffect, useReducer, useRef} from 'react';
import {
    closestCenter,
    CollisionDetection,
    getFirstCollision,
    pointerWithin,
    rectIntersection,
    UniqueIdentifier
} from '@dnd-kit/core';
import {arrayMove} from '@dnd-kit/sortable';

type Items = Map<UniqueIdentifier, UniqueIdentifier[]>;

type Action<T, P = undefined> = {
    type: T;
    payload?: P;
};

export enum ActionTypes {
    ITEMS_UPDATE = 'ITEMS_UPDATE',
    STATE_UPDATE = 'STATE_UPDATE'
}

function reducer(state: State, action: Action<ActionTypes, any>): State {
    switch (action.type) {
        case ActionTypes.ITEMS_UPDATE:
            // Prevent unnecessary state updates
            if (state.itemsMap === action.payload) {
                return state;
            }

            return {
                ...state,
                itemsMap: action.payload
            };
        default:
            return {
                ...state,
                ...(action.payload || {})
            };
    }
}

type State = {
    activeId: UniqueIdentifier | null;
    itemsMap: Items;
    clonedItems: Items | null;
};

type HookProps = {
    initialItemsMap: Map<UniqueIdentifier, UniqueIdentifier[]>;
    onChange: (itemsMap?: Map<UniqueIdentifier, UniqueIdentifier[]>) => void;
};

export const useHomepageState = (props: HookProps) => {
    const {initialItemsMap, onChange} = props;
    const [state, dispatch] = useReducer(reducer, {
        clonedItems: null,
        activeId: null,
        itemsMap: initialItemsMap
    });

    const {clonedItems, itemsMap, activeId} = state;

    const lastOverId = useRef<UniqueIdentifier | null>(null);
    const recentlyMovedToNewContainer = useRef(false);
    const activeItemTypeRef = useRef(null);

    const isActiveChangeContainer = useRef(false);

    const findContainer = (id: UniqueIdentifier) => {
        if (itemsMap.has(id)) {
            return id;
        }

        return [...itemsMap.keys()].find(key => itemsMap.get(key)?.includes(id));
    };

    const getIndex = (id: UniqueIdentifier) => {
        const container = findContainer(id);

        if (!container) return -1;

        return itemsMap.get(container).indexOf(id);
    };

    const onDragCancel = useCallback(() => {
        activeItemTypeRef.current = null;
        isActiveChangeContainer.current = false;

        if (clonedItems) {
            // Reset items to their original state in case items have been
            // Dragged across containers
            dispatch({
                type: ActionTypes.STATE_UPDATE,
                payload: {
                    activeId: null,
                    clonedItems: null
                }
            });
        }
    }, [clonedItems]);

    useEffect(() => {
        dispatch({type: ActionTypes.ITEMS_UPDATE, payload: initialItemsMap});
    }, [initialItemsMap]);

    useEffect(() => {
        requestAnimationFrame(() => {
            recentlyMovedToNewContainer.current = false;
        });
    }, [itemsMap]);

    const onDragStart = e => {
        activeItemTypeRef.current = e.active?.data?.current?.itemType;

        dispatch({
            type: ActionTypes.STATE_UPDATE,
            payload: {
                clonedItems: new Map(itemsMap),
                activeId: e.active.id
            }
        });
    };

    const onDragOver = ({active, over}) => {
        const overId = over?.id;

        if (overId == null || itemsMap.has(active.id)) {
            return;
        }

        const overContainer = findContainer(overId);
        const activeContainer = findContainer(active.id);

        if (!overContainer || !activeContainer) {
            return;
        }

        if (
            activeContainer !== overContainer &&
            over.data.current?.itemType === activeItemTypeRef.current
        ) {
            const overItems = itemsMap.get(overContainer);
            const overIndex = overItems.indexOf(overId);

            isActiveChangeContainer.current = true;
            let newIndex: number;

            if (overId in itemsMap) {
                newIndex = overItems.length + 1;
            } else {
                const isBelowOverItem =
                    over &&
                    active.rect.current.translated &&
                    active.rect.current.translated.top > over.rect.top + over.rect.height;

                const modifier = isBelowOverItem ? 1 : 0;

                newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
            }

            recentlyMovedToNewContainer.current = true;

            const res = new Map(itemsMap);
            res.set(
                activeContainer,
                itemsMap.get(activeContainer).filter(item => item !== active.id)
            ); // Remove the item from the active container
            res.set(overContainer, [
                ...overItems.slice(0, newIndex),
                active.id,
                ...overItems.slice(newIndex, overItems.length)
            ]); // Add the item to the new container

            dispatch({type: ActionTypes.STATE_UPDATE, payload: {itemsMap: res}});
        }
    };

    const onDragEnd = ({active, over}) => {
        const data: Partial<State> = {};

        if (itemsMap.has(active.id) && over?.id) {
            const containers = [...itemsMap.keys()];
            const activeIndex = containers.indexOf(active.id);
            const overIndex = containers.indexOf(over.id);
            const movedContainers = arrayMove(containers, activeIndex, overIndex);

            data.itemsMap = new Map(movedContainers.map(id => [id, itemsMap.get(id)]));
        }

        const activeContainer = findContainer(active?.id);
        const overContainer = findContainer(over?.id);
        const activeItemType = active.data.current?.itemType || activeItemTypeRef.current;

        if (activeContainer && overContainer && activeItemType === over.data.current?.itemType) {
            const activeItems = itemsMap.get(activeContainer);
            const overItems = itemsMap.get(overContainer);

            const activeIndex = activeItems.indexOf(active.id);
            const overIndex = overItems.indexOf(over?.id);

            if (activeIndex !== -1 && overIndex !== -1) {
                // Move item within the new container using arrayMove
                const updatedOverItems = arrayMove(overItems, activeIndex, overIndex);

                // Create a new Map with the updated items
                const newItemsMap = new Map(itemsMap);

                // Remove the item from the active container and update the new container
                newItemsMap.set(
                    activeContainer,
                    activeItems.filter(item => item !== active.id)
                );
                newItemsMap.set(overContainer, updatedOverItems);

                data.itemsMap = newItemsMap;
            } else {
                /* update the itemsMap if the number of items in the overContainer has changed.
                 * It can happen when an item is moved to a new container and `over.id` is a container id
                 * and `overIndex` is -1.
                 */
                if (overItems.length !== clonedItems.get(overContainer).length) {
                    data.itemsMap = itemsMap;
                }
            }
        }

        if (isActiveChangeContainer.current === true) {
            isActiveChangeContainer.current = false;
        }

        if (data.itemsMap) {
            dispatch({type: ActionTypes.STATE_UPDATE, payload: {activeId: null, ...data}});
            onChange(data.itemsMap);
        }
        activeItemTypeRef.current = null;
    };

    /*
     * Custom collision detection strategy optimized for multiple containers
     * - First, find any droppable containers intersecting with the pointer.
     * - If there are none, find intersecting containers with the active draggable.
     * - If there are no intersecting containers, return the last matched intersection
     */

    const collisionDetectionStrategy: CollisionDetection = useCallback(
        args => {
            if (activeId && activeId in itemsMap) {
                return closestCenter({
                    ...args,
                    droppableContainers: args.droppableContainers.filter(
                        container => container.id in itemsMap
                    )
                });
            }

            // Start by finding any intersecting droppable
            const pointerIntersections = pointerWithin(args);
            const intersections =
                pointerIntersections.length > 0
                    ? // If there are droppable intersecting with the pointer, return those
                      pointerIntersections
                    : rectIntersection(args);
            let overId = getFirstCollision(intersections, 'id');

            if (overId != null) {
                if (overId in itemsMap) {
                    const containerItems = itemsMap.get(overId);

                    // If a container is matched and it contains items
                    if (containerItems.length > 0) {
                        // Return the closest droppable within that container
                        overId = closestCenter({
                            ...args,
                            droppableContainers: args.droppableContainers.filter(
                                container =>
                                    container.id !== overId && containerItems.includes(container.id)
                            )
                        })[0]?.id;
                    }
                }

                lastOverId.current = overId;
                return [{id: overId}];
            }

            // When a draggable item moves to a new container, the layout may shift
            // and the `overId` may become `null`. We manually set the cached `lastOverId`
            // to the id of the draggable item that was moved to the new container, otherwise
            // the previous `overId` will be returned which can cause items to incorrectly shift positions
            if (recentlyMovedToNewContainer.current) {
                lastOverId.current = activeId;
            }

            // If no droppable is matched, return the last match
            return lastOverId.current ? [{id: lastOverId.current}] : [];
        },
        [activeId, itemsMap]
    );

    return {
        state,
        handlers: {
            onDragCancel,
            onDragStart,
            onDragOver,
            onDragEnd,
            getIndex,
            findContainer,
            collisionDetectionStrategy
        }
    };
};
